import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { DollarSign, Clock, Shield, TrendingUp, Truck, Package } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Drive = () => {
  const navigate = useNavigate();
  const [logisticsVisible, setLogisticsVisible] = useState(false);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const api = (await import('../utils/api')).default;
        const res = await api.get('/feature-flags');
        setLogisticsVisible(res.data.logistics_visible);
      } catch (e) {}
    };
    fetchFlags();
  }, []);

  const handleApplyToDrive = () => {
    // Check if user is logged in
    const token = localStorage.getItem('tekstar_token');
    
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login or signup first, then apply as a driver',
      });
      // Store intent to apply as driver
      sessionStorage.setItem('redirect_after_login', '/driver/apply');
      navigate('/login');
      return;
    }
    
    // Navigate to driver application
    navigate('/driver/apply');
  };

  const handleLearnMore = () => {
    // Scroll to benefits section
    const benefitsSection = document.getElementById('benefits-section');
    if (benefitsSection) {
      benefitsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Drive with
                <br />
                <span className="text-purple-600">Tekstar</span>
              </h1>
              <p className="text-xl text-gray-600">
                Set your own schedule, earn extra money, and meet new people.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={handleApplyToDrive}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg font-semibold rounded-xl"
                >
                  Apply to drive
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/driver/login')}
                  className="px-8 py-6 text-lg font-semibold rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  Sign in as driver
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Already a driver?{' '}
                <button
                  onClick={() => navigate('/driver/login')}
                  className="text-purple-600 hover:text-purple-700 font-medium underline"
                >
                  Sign in here
                </button>
              </p>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-300 to-pink-300 rounded-3xl blur-3xl opacity-20"></div>
                <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                  <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
                    <div className="text-8xl">🚗</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings calculator */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
            <div className="text-center space-y-4">
              <h2 className="text-3xl font-bold">Earnings Calculator</h2>
              <p className="text-purple-100 text-lg">See how much you could make</p>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-4xl font-bold">K1,200</div>
                  <div className="text-purple-100 mt-2">Per week average</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-4xl font-bold">20</div>
                  <div className="text-purple-100 mt-2">Hours per week</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-4xl font-bold">K60</div>
                  <div className="text-purple-100 mt-2">Per hour average</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Benefits */}
      <div id="benefits-section" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why drive with Tekstar?
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of drivers earning on their own terms
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Earn more</h3>
              <p className="text-gray-600">
                Keep 100% of your tips and earn weekly bonuses.
              </p>
            </Card>

            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full">
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Set your schedule</h3>
              <p className="text-gray-600">
                Drive whenever you want, as much or as little as you like.
              </p>
            </Card>

            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full">
                <Shield className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Stay protected</h3>
              <p className="text-gray-600">
                K1M liability insurance and 24/7 support for all drivers.
              </p>
            </Card>

            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Get paid fast</h3>
              <p className="text-gray-600">
                Cash out instantly or get paid weekly with direct deposit.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Requirements</h2>
            <p className="text-xl text-gray-600">What you need to get started</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Driver requirements</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>At least 21 years old</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>Valid driver's license</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>1 year of driving experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>Clean driving record</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Vehicle requirements</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>4-door vehicle</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>2008 or newer</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>In-state license plates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>Pass vehicle inspection</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl lg:text-4xl font-bold text-white">
            Ready to start driving?
          </h2>
          <p className="text-xl text-purple-100">
            Apply now and start earning in just a few days.
          </p>
          <Button
            size="lg"
            onClick={handleApplyToDrive}
            className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-xl"
          >
            Apply to drive
          </Button>
        </div>
      </div>

      {/* Cargo Operators Section */}
      {logisticsVisible && (
      <div className="py-16 px-4 bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                <Truck className="h-4 w-4" />
                Cargo Transportation
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Own a truck?
                <br />
                <span className="text-orange-600">Earn with Tekstar Logistics</span>
              </h2>
              <p className="text-xl text-gray-600">
                Join our cargo transportation network. Whether you have a pickup truck, 
                mini truck, or large truck - start earning by delivering goods across Zambia.
              </p>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <p className="text-3xl font-bold text-orange-600">K2,500</p>
                  <p className="text-sm text-gray-600">Weekly avg. earnings</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <p className="text-3xl font-bold text-orange-600">100%</p>
                  <p className="text-sm text-gray-600">Tips you keep</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/operator/apply')}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold rounded-xl"
                  data-testid="apply-operator-btn"
                >
                  <Package className="mr-2 h-5 w-5" />
                  Apply as Cargo Operator
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/operator/login')}
                  className="px-8 py-6 text-lg font-semibold rounded-xl border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="operator-login-btn"
                >
                  <Truck className="mr-2 h-5 w-5" />
                  Operator Login
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-orange-100">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 mb-4">Vehicle Types Accepted</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
                      <span className="text-3xl">🛻</span>
                      <div>
                        <p className="font-medium">Pickup Truck</p>
                        <p className="text-sm text-gray-600">Up to 1,000 kg capacity</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
                      <span className="text-3xl">🚚</span>
                      <div>
                        <p className="font-medium">Mini Truck</p>
                        <p className="text-sm text-gray-600">1,000 - 3,000 kg capacity</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
                      <span className="text-3xl">🚛</span>
                      <div>
                        <p className="font-medium">Large Truck</p>
                        <p className="text-sm text-gray-600">5,000+ kg capacity</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg">
                      <span className="text-3xl">🚐</span>
                      <div>
                        <p className="font-medium">Cargo Van</p>
                        <p className="text-sm text-gray-600">Small loads & parcels</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Drive;